import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// Components
import Layout from '../../components/layouts/Basic/Layout';
import Header from '../../components/layouts/Header/Header';
// Helpers
import { isInternalUser } from '../../helpers/authorization';
import { useAppSelector } from '../../store';
// Hooks
import { useGetPublisherByIdMutation } from './publisherApiSlice';
// Reducers
import { setPublisher } from './publisherReducer';
import { useSearchPublishersMutation } from './publishersApiSlice';
import styles from './Browse.module.scss';
import { publishersSearchClear, setPublishers } from './publishersReducer';
import Loader from '../../components/common/Loader';
import PublisherSelection from './PublisherSelection';
import { useToggle } from 'usehooks-ts';

// A location passed in as `from` that points back at the picker or the login page would
// just recreate the redirect it is meant to resolve, so those are never honoured here;
// the normal '/home/reporting' fallback is used instead.
const LOOP_PRONE_PATHS = ['/', '/browse', '/login'];

const Browse = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [isFirstLoad, , setIsFirstLoad] = useToggle(true);

    const [getPublisherById] = useGetPublisherByIdMutation();
    const [searchPublishers, { isLoading: isSearchingPublishers }] = useSearchPublishersMutation();

    const publishers = useAppSelector(state => state.publishers);
    const user = useAppSelector(state => state.auth);
    const isInternal = isInternalUser(user);

    // The deep link (if any) that sent the user through this picker, carried here as
    // router state from Routes.jsx's mount effect. Once a publisher is actually
    // resolved below, either by auto-selecting the sole publisher an external user has,
    // or by the user explicitly choosing one, send them on to it instead of always
    // landing on /home/reporting. This is deliberate: a single-publisher user never
    // sees the picker at all in that case, while a multi-publisher (typically internal)
    // user sees it once, because the app genuinely cannot know which publisher context
    // to use until they choose.
    const requestedFrom = location.state?.from;
    const destination =
        requestedFrom && !LOOP_PRONE_PATHS.includes(requestedFrom.pathname)
            ? requestedFrom
            : '/home/reporting';

    useEffect(() => {
        if (isInternal) {
            dispatch(publishersSearchClear());
        }

        // redirect to publisher select or reporting
        // depending on the number of publishers the user is linked to
        searchPublishers({})
            .unwrap()
            .then(publishers => {
                dispatch(setPublishers(publishers));
                setIsFirstLoad(false);
                if (publishers.length === 1) {
                    return getPublisherById(publishers[0].id)
                        .unwrap()
                        .then(publisher => {
                            dispatch(setPublisher(publisher));
                            // Navigate only after the publisher has landed in the
                            // store; navigating first (as this used to) could mount
                            // the destination page with no publisher yet, bouncing it
                            // right back to the picker.
                            navigate(destination);
                        });
                }
            });
    }, []);

    const handleSearch = async (searchTerm: string) => {
        const publishers = await searchPublishers({ searchTerm }).unwrap();
        dispatch(setPublishers(publishers));
    };

    const handleSelect = async (value: number) => {
        const publisher = await getPublisherById(value).unwrap();
        dispatch(setPublisher(publisher));

        const publishers = await searchPublishers({}).unwrap();
        dispatch(setPublishers(publishers));

        return navigate(destination);
    };

    return (
        <Layout>
            <Header />
            <div className={styles.content} data-cy="browse">
                {isFirstLoad ? (
                    <Loader show={isSearchingPublishers} />
                ) : (
                    <PublisherSelection
                        publishers={publishers}
                        isLoading={isSearchingPublishers}
                        handleSearch={handleSearch}
                        handleSelect={handleSelect}
                    />
                )}
            </div>
        </Layout>
    );
};

export default Browse;
